import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:5000/api';

// Track auth-related state
const authState = {
  isRedirecting: false,
  lastAuthCheck: 0,
  MIN_AUTH_CHECK_INTERVAL: 2000 // 2 seconds
};

// Main API instance with authentication
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Create a separate instance for public requests that doesn't send credentials
export const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: false // Don't send cookies/credentials with these requests
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Don't intercept auth-related requests
    if (config.url.includes('/auth/')) {
      return config;
    }

    // For profile checks, ensure we don't check too frequently
    if (config.url.includes('/users/profile')) {
      const now = Date.now();
      if (now - authState.lastAuthCheck < authState.MIN_AUTH_CHECK_INTERVAL) {
        return Promise.reject(new axios.Cancel('Auth check throttled'));
      }
      authState.lastAuthCheck = now;
    }

    // Check if this is a request to a public endpoint from a public page
    const publicPages = ['/team', '/sponsors'];
    const publicEndpoints = ['/sponsors', '/galleries/random-image'];
    
    const isPublicPage = publicPages.some(page => window.location.pathname === page);
    const isPublicEndpoint = publicEndpoints.some(endpoint => config.url.includes(endpoint));
    
    if (isPublicPage && isPublicEndpoint) {
      // Remove authentication headers for public requests from public pages
      if (config.headers) {
        delete config.headers.Authorization;
      }
      // Skip sending credentials (cookies) for these requests
      config.withCredentials = false;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't handle cancelled requests
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const message = error.response?.data?.message || 'An error occurred';
    
    // Define public routes that don't require authentication
    const publicRoutes = ['/', '/events-landing', '/login', '/register', '/team', '/sponsors'];
    const isPublicRoute = publicRoutes.includes(window.location.pathname);

    // Define public API endpoints that shouldn't trigger redirects
    const publicEndpoints = [
      '/api/users/public',
      '/api/cars/public',
      '/api/galleries/random-image',
      '/api/events/public',
      '/api/sponsors'
    ];
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      error.config.url.includes(endpoint)
    );

    // Skip auth handling for:
    // 1. Auth-related endpoints
    // 2. Public endpoints listed above
    // 3. If we're already in the process of redirecting
    // 4. If on a public route
    // 5. Add special handling for sponsors page
    if (error.config.url.includes('/auth/') || 
        isPublicEndpoint || 
        authState.isRedirecting || 
        isPublicRoute || 
        (window.location.pathname === '/sponsors' && 
         (error.config.url.includes('/sponsors') || error.config.url.includes('/galleries')))
    ) {
      return Promise.reject(error);
    }

    switch (status) {
      case 401:
        // Only redirect if we're not already on the login page
        if (window.location.pathname !== '/login') {
          authState.isRedirecting = true;
          toast.error('Session expired. Please login again.');
          sessionStorage.setItem('redirectTo', window.location.pathname);
          window.location.href = '/login';
        }
        break;
      case 403:
        toast.error('You do not have permission to perform this action');
        break;
      case 404:
        toast.error('Resource not found');
        break;
      case 422:
        toast.error(message);
        break;
      case 500:
        toast.error('Server error. Please try again later.');
        break;
      default:
        toast.error(message);
    }

    return Promise.reject(error);
  }
);

// Reset redirect flag when page loads
window.addEventListener('load', () => {
  authState.isRedirecting = false;
});

export default api; 